import { CommonModule, DatePipe } from '@angular/common';
import {
  Component,
  computed,
  inject,
  input,
  OnInit,
  signal,
  WritableSignal,
  effect,
  untracked,
  ChangeDetectionStrategy
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ApiService } from '../../../../core/services/api.service';
import { CustomDashboardPinService } from '../../../../core/services/custom-dashboard-pin.service';
import {
  DashboardPageEngine,
  type DashboardEngineConfig,
} from '../../../../core/services/dashboard-page-engine';
import {
  MonthlyReport1CardId,
  MONTHLY_REPORT_1_CARD_CONFIG,
  isMonthlyReport1CardId,
  resolveMonthlyReport1CardFromWidgetKey,
  type DashboardCardLayoutItem,
} from '../../../../core/enums/dashboard-cards.enum';
import { CdkDragDrop, DragDropModule } from '@angular/cdk/drag-drop';
import {
  applyLayoutEditorCardDrop,
  computeLayoutEditorAutoWidthUpdates,
  reorderLayoutEditorItemsOnVisibilityToggle,
} from '../../../../shared/components/dashboard/dashboard-layout-editor.helpers';
import { stripProductPrefix } from '../../../../shared/components/dashboard/dashboard-embed.helpers';
import { environment } from '../../../../../environments/environment';
import {
  DatePickerComponent,
  DateRangePickerOutput,
  ComparisonType,
} from '../../../../shared/components/date-picker/date-picker.component';
import { LineChartComponent } from '../../../../shared/components/charts/line-chart/line-chart.component';
import { ToggleBtnsComponent } from '../../../../shared/components/toggle-btns/toggle-btns.component';
import { SingleBarComponent } from '../../../../shared/components/charts/bar-charts/single-bar/single-bar.component';
import { UnitSuggestionService } from '../../../../core/services/unit-suggestion.service';
import { ProductConfigService } from '../../../../core/services/product-config.service';
import { TableHeader } from '../../../../shared/components/tables/scrolled-table/scroll-table.component';
import { IconComponent, IconName } from '../../../../shared/icons/icon.component';
import { PaginatedTableComponent } from '../../../../shared/components/tables/paginated-table/paginated-table.component';
import { MatDialogModule } from '@angular/material/dialog';
import { AiSummaryModalComponent } from '../../../../shared/components/ai-summary-modal/ai-summary-modal.component';
import { IndividualToasterComponent } from '../../../../shared/components/loader/loader.component';
import { PieChartComponent } from '../../../../shared/components/charts/pie-chart/pie-chart.component';
import { HorizontalBarChartComponent } from '../../../../shared/components/charts/horizontal-bar-chart/horizontal-bar-chart.component';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import dayjs from 'dayjs';
import { MultipleBarComponent } from "../../../../shared/components/charts/bar-charts/multiple-bar/multiple-bar.component";
import { CommonService } from '../../../../core/services/common.service';
import { BreakpointObserver } from '@angular/cdk/layout';
import { IconComponentV2 } from '../../../../shared/components/icon-component/icon.component';
import { MatBottomSheet, MatBottomSheetModule } from '@angular/material/bottom-sheet';
import { MonthlyReportMobileComponent } from './mobile-view/monthly-report-mobile.component';
import { MobileSheetComponent, MobileSheetData } from '../../../../shared/components/mobile-sheet/mobile-sheet.component';
import { AiChatPanelComponent } from '../../../../pages/public/chatbot/ai-chat-panel/ai-chat-panel.component';
import { AiBotService } from '../../../../core/services/ai-bot.service';
import { HeadlinePageService } from '../../../../core/services/headline-page.service';
import { BreakpointService } from '../../../../core/services/breakpoint.service';
import { AiHeadlinesComponent } from '../../../../shared/components/ai-headlines/ai-headlines.component';
dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

const LA_TIMEZONE = 'America/Los_Angeles';



interface AISummaryData {
  summary: string;
  insights: {
    label: string;
    evidence: string;
    value: string;
    severity: 'high' | 'med' | 'low';
  }[];
  playbook: {
    step: string;
    owner: string;
    timeframe: string;
    expected_change: string;
    effort?: string;
  }[];
  actions: string[];
  anomalies: string[];
}

/** Draft row used by the inline Customize Layout editor. */
interface LayoutEditorItem {
  id: MonthlyReport1CardId;
  title: string;
  visible: boolean; 
  colSpan: number; 
  rowSpan: number;
  order: number;
  widgetId: number;
  placementId?: number;
  sourcePageId: number | null;
  customConfig: Record<string, any> | null;
}

import { FormsModule } from '@angular/forms';

@Component({
  standalone: true,
  selector: 'app-monthly-report-1',
  templateUrl: './monthly-report-1.component.html',
  styleUrls: ['./monthly-report-1.component.scss'],
  imports: [CommonModule, FormsModule, DragDropModule, DatePickerComponent, LineChartComponent, ToggleBtnsComponent, SingleBarComponent, IconComponent, PaginatedTableComponent, MatDialogModule, IndividualToasterComponent, HorizontalBarChartComponent, PieChartComponent, MultipleBarComponent, MatBottomSheetModule, MonthlyReportMobileComponent, AiHeadlinesComponent,IconComponentV2],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [DatePipe],
})
export class MonthlyReport1Component extends DashboardPageEngine<MonthlyReport1CardId> implements OnInit {
  // ─── Engine config (required by DashboardPageEngine) ────────────────────
  protected readonly dashboardEngineConfig: DashboardEngineConfig<MonthlyReport1CardId> = {
    pageId: 1,
    cardConfig: MONTHLY_REPORT_1_CARD_CONFIG,
    isCardId: isMonthlyReport1CardId,
    resolveCardId: resolveMonthlyReport1CardFromWidgetKey,
    mixedCatalogPageIds: [1, 2, 3],
    hiddenLayoutActions: {
      saveAs: true,
      reset: true,
      delete: true,
      widgets: true,
    },
  };

  // ─── Component-level services ────────────────────────────────────────────
  private apiService = inject(ApiService);
  private route = inject(ActivatedRoute);
  private datePipe = inject(DatePipe);
  private unitService = inject(UnitSuggestionService);
  private productConfigService = inject(ProductConfigService);
  private breakpointObserver = inject(BreakpointObserver);
  private aiBotService = inject(AiBotService);
  private headlinePageService = inject(HeadlinePageService);
  private router = inject(Router);
  private _bottomSheet = inject(MatBottomSheet);
  public _breakpointService = inject(BreakpointService);
  private customDashboardPin = inject(CustomDashboardPinService);
 
  readonly addedToCustomDashboard = signal<Set<MonthlyReport1CardId>>(new Set());
  readonly addingToCustomDashboardId = signal<MonthlyReport1CardId | null>(null);
 
  private _hydratedCustomDashboardForProductId: string | number | null = null;
  readonly mixedMode = input<boolean>(false);
  readonly visibleWidgetKeys = input<string[] | null>(null);

  readonly filteredDashboardCards = computed(() => {
    const keys = this.visibleWidgetKeys();
    const all = this.mixedMode()
      ? this.allAllowedDashboardCards()
      : this.orderedDashboardCards();
    if (!keys || keys.length === 0) return all;

    const allowed = new Set<MonthlyReport1CardId>();
    for (const key of keys) {
      const resolved = resolveMonthlyReport1CardFromWidgetKey(key);
      if (resolved) allowed.add(resolved);
    }
    if (allowed.size === 0) return all;

    return all.filter((c) => allowed.has(c.id));
  });

  /**
   * Cards the host of this report is currently allowed to render. When
   * the component is the standalone page (`mixedMode() === false`) every
   * card is considered active. When embedded by `WidgetHostComponent`
   * (My Dashboard), only the cards matching `visibleWidgetKeys` are
   * active — used below to skip fan-out API calls for cards the host
   * never renders.
   *
   * IMPORTANT: derived directly from `visibleWidgetKeys()` (synchronous
   * host input), NOT from `filteredDashboardCards()` — the latter depends
   * on the personalized layout (`orderedDashboardCards`) which is loaded
   * asynchronously, so it is empty at the moment the productId effect
   * fires `fetchAllReports()`. Using the layout-derived set would cause
   * every loader to be rejected on first paint.
   */
  private readonly activeCardIds = computed<ReadonlySet<MonthlyReport1CardId>>(() => {
    const keys = this.visibleWidgetKeys();
    if (!keys || keys.length === 0) return new Set();
    const allowed = new Set<MonthlyReport1CardId>();
    for (const key of keys) {
      if (!key) continue;
      const resolved = resolveMonthlyReport1CardFromWidgetKey(key);
      if (resolved) {
        allowed.add(resolved);
        continue;
      }
      const stripped = stripProductPrefix(key);
      if (stripped !== key) {
        const resolvedStripped = resolveMonthlyReport1CardFromWidgetKey(stripped);
        if (resolvedStripped) allowed.add(resolvedStripped);
      }
    }
    return allowed;
  });

  /** Returns true if any of the given cards is currently rendered. */
  private isSectionActive(...sectionIds: MonthlyReport1CardId[]): boolean {
    if (!this.mixedMode()) return true;
    const active = this.activeCardIds();
    // Defensive: when host did not send any keys (or none resolved), fall
    // back to "active" so we never accidentally suppress a legitimate
    // request. The host always sends a single key per mounted tile, so an
    // empty set only happens for unrecognised widget keys.
    if (active.size === 0) return true;
    return sectionIds.some((id) => active.has(id));
  }

  selectedProductId = signal<string | null>(null);
  /** Increments on each product-selection effect run; tags in-flight report fetches. */
  private reportFetchGeneration = 0;
  isDenefits = computed(() => {
    const productId = this.selectedProductId();
    if (!productId) return false;
    return this.productConfigService.getProductName(productId).toLowerCase() === 'denefits';
  });
  sectionLoading = signal<Record<string, boolean>>({});

  public _commonService = inject(CommonService);
  isLoading = signal(false);
  Loading = signal(false);

  /** Expose card ID enum to the template. */
  readonly CardId = MonthlyReport1CardId;

  isSectionLoading(section: string): boolean {
    return this.sectionLoading()[section] || false;
  }

  setSectionLoading(section: string, isLoading: boolean): void {
    this.sectionLoading.update(prev => ({ ...prev, [section]: isLoading }));
  }

  constructor() {
    super(); // initialises DashboardPageEngine (saving$ subscription, signals)
 
    effect(() => {
      const mixed = this.mixedMode();
      untracked(() => {
        if (this.isMixedDashboard() !== mixed) {
          this.isMixedDashboard.set(mixed);
        }
      });
    });

    // React to global product-selection changes
    effect(() => {
      const globalProductId = this.apiService.selectedProductId();

      if (globalProductId) {
        untracked(() => {
          this.reportFetchGeneration += 1;
          this.selectedProductId.set(globalProductId);
          // My Dashboard (`*ngComponentOutlet inputs: { mixedMode: true }`).
          const isMixed = this.mixedMode();
          if (this.isMixedDashboard() !== isMixed) {
            this.isMixedDashboard.set(isMixed);
          }
 
          // `cachedProductId`, the next call into the override will still re-sync, but at least our initial value is correct and the engine's bootstrap path can never fire with `cachedProductId == null`.
          this._syncCachedProductIdToSelectedProduct(globalProductId);

          if (isMixed) {
            this.ensureDefaultDateRangeForMixedMode();
          }

          this.initializeTableSections();
          this.triggerRecentFetch();
          this.fetchAllReportsWithoutTimePeriod();
          this.loadPersonalizedDashboardLayout(); // overridden below
        });
      }
    });
 
    effect(() => {
      const pid = this.cachedProductId();
      if (pid == null) return;
      if (this._hydratedCustomDashboardForProductId === pid) return;
      if (this.mixedMode()) return;

      untracked(() => {
         
        const selectedCuid = this.apiService.selectedProductId();
        const expectedPid = this.resolveActiveProductPid(selectedCuid);
        if (
          expectedPid != null &&
          String(pid) !== String(expectedPid)
        ) { 
          return;
        }

        this._hydratedCustomDashboardForProductId = pid;
        this.addedToCustomDashboard.set(new Set());
        this.hydrateAddedToCustomDashboardFromServer(pid);
      });
    });

    this.breakpointObserver
      .observe(['(max-width: 1536px)'])
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((result) => {
        if (result.matches) {
          this.barWidthScale.set(1);
        } else {
          this.barWidthScale.set(2);
        }
      });
  
    effect(() => {
      const loading = this.layoutLoading();
      const pendingCuid = this._pendingReloadForCuid();

      if (!loading && pendingCuid != null) {
        untracked(() => { 
          this._pendingReloadForCuid.set(null);
          this.loadPersonalizedDashboardLayout();
        });
      }
    });
  
    effect(() => {
      const cached = this.cachedProductId();
      const selectedCuid = this.apiService.selectedProductId();
      if (selectedCuid == null) return;

      untracked(() => {
        const expected = this.resolveActiveProductPid(selectedCuid);
        if (expected == null) return;
        if (String(cached ?? '') === String(expected)) return;
        this.cachedProductId.set(expected);
      });
    });
  }
 
  private _pendingReloadForCuid = signal<string | number | null>(null);
 
  private resolveActiveProductPid(
    selectedCuid: string | number | null,
  ): string | number | null {
    const isNumericPid = (v: unknown): boolean =>
      v != null && /^\d+$/.test(String(v));

    if (selectedCuid == null) return null;

    try {
      const stored = sessionStorage.getItem('dynamic_routes');
      if (stored) {
        const allProducts = JSON.parse(stored) as any[];
        const matchedRoute = allProducts.find(
          (p: any) => String(p?.product?.id) === String(selectedCuid),
        );
        const pidFromRoutes = matchedRoute?.product?.pid;
        if (isNumericPid(pidFromRoutes)) return pidFromRoutes;
      }
    } catch (err) {
    }

    const products = this.apiService.products() ?? [];
    if (products.length > 0) {
      const fromProducts = products.find(
        (p: any) => String(p?.id) === String(selectedCuid),
      );
      const pidFromProducts = fromProducts?.pid;
      if (isNumericPid(pidFromProducts)) return pidFromProducts;
    }

    const fromSignal = this.apiService.selectedProductPid();
    if (isNumericPid(fromSignal)) return fromSignal;

    return null;
  }
 
  private _syncCachedProductIdToSelectedProduct(
    selectedCuid: string | number | null,
  ): string | number | null {
    const resolvedPid = this.resolveActiveProductPid(selectedCuid);
    const nextValue: string | number | null = resolvedPid ?? selectedCuid;
 
    if (
      resolvedPid != null &&
      String(this.apiService.selectedProductPid() ?? '') !== String(resolvedPid)
    ) {
      this.apiService.setSelectedProductPid(resolvedPid);
    }

    if (nextValue != null && this.cachedProductId() !== nextValue) {
      this.cachedProductId.set(nextValue);
    }

    return nextValue;
  }
 
  override loadPersonalizedDashboardLayout(): void {
    const selectedCuid = this.apiService.selectedProductId();
    const isLoading = this.layoutLoading();
    const isMixed = this.isMixedDashboard();
   
    this._syncCachedProductIdToSelectedProduct(selectedCuid);

    // ── Bootstrap guard ─────────────────────────────────────────────── 
    
    if (isMixed && this.cachedProductId() == null) {
      // Queue a retry keyed on the cuid so the next time a product is resolved (sidebar restore, products API hydration, etc.) we re-run and fire the mixed call with the correct pid.
      this._pendingReloadForCuid.set(selectedCuid ?? 'pending');
      return;
    }

    if (isLoading) { 
      this._pendingReloadForCuid.set(selectedCuid);
      return;
    }

    super.loadPersonalizedDashboardLayout();
  }

  private triggerRecentFetch() {
    if (this.start_date && this.end_date) {
        const productId = this.selectedProductId();
        if(!productId) return;
        
        const productConfig = this.productConfigService.getProductConfig(this.productConfigService.getProductName(productId));
        let payload: any = {
            start_time: this.start_date,
            end_time: this.end_date,
            unit: this.unit(),
            overall: 1,
            ...productConfig.payloadExtras,
        };
        this.fetchAllReports(payload);
        
    }
  }
 
  private ensureDefaultDateRangeForMixedMode(): void {
    if (this.start_date && this.end_date) return;

    const end = dayjs.utc().tz(LA_TIMEZONE);
    const start = end.subtract(29, 'day');

    this.current_start_date = start.format('YYYY-MM-DD');
    this.current_end_date = end.format('YYYY-MM-DD');
    this.start_date = this.current_start_date;
    this.end_date = this.current_end_date;
    this.unit.set('day');
    this.isPrevious = false;
  }
  activeAnalysisRequests = signal<Set<string>>(new Set());
  overallContractTypeSignal = signal<any[]>([]);
  enrolledBusinessSignal = signal<any[]>([]);
  industryDataSignal = signal<any[]>([]);
  regionalContractsSignal = signal<any[]>([]);

  businessWiseContractSignal = signal<any[]>([]);
  overallBusinessContractSignal = signal<any[]>([]);
  unit = signal('day')
  barWidthScale = signal(2);
  
  // Current timestamp for display
  get currentTimestamp(): string {
    return new Date().toLocaleString();
  }

  get issueId(): string {
    return `MR2-${Date.now()}`;
  }
  activeBusiness: any = {};
  pieChartData: any = {};
  topCategoriesChartData: any = {};
  businessWiseContractChartData: any = {};
  contactChartData: any = {};

  businessContractTypes: any = [];
  businessWiseContract: any = {
    page: 1,
    limit: 10,
    total: 0,
    data: [],
  };

  overallBusinessContract: any = {
    page: 1,
    limit: 10,
    total: 0,
    data: [],
  };
  tablesSections: any[] = [];
  start_date: any;
  end_date: any;
  current_start_date: any;
  current_end_date: any;
  aiSummaryData: AISummaryData = {
    summary: '',
    insights: [],
    playbook: [],
    actions: [],
    anomalies: [],
  };
  tabOptions = [
    {
      label: 'Line Chart',
      tooltip: 'Click to View Line Graph',
      isTable: false,
      value: 'lineChart',
      iconName: 'chartLine' as IconName,
    },
    {
      label: 'Bar Chart',
      tooltip: 'Click to View Bar Graph',
      isTable: true,
      value: 'barChart',
      iconName: 'chartColumn' as IconName,
    },
  ];
  categoryTabOptions = [
    {
      label: 'Table',
      tooltip: 'Click to View Table',
      isTable: true,
      value: 'table',
      icon: 'assets/img/PNG/show_table.png',
    },
    {
      label: 'Graph',
      tooltip: 'Click to View Bar Graph',
      isTable: false,
      value: 'barChart',
      icon: 'assets/img/PNG/Vector.png',
      iconName: 'chartBar' as IconName,
    },
  ];
  // contactTabOptions = [
  //   {
  //     label: 'Table',
  //     tooltip: 'Click to View Table',
  //     isTable: true,
  //     value: 'table',
  //     icon: 'assets/img/PNG/show_table.png'
  //   },
  //   {
  //     label: 'Graph',
  //     tooltip: 'Click to View Bar Graph',
  //     isTable: false,
  //     value: 'barChart',
  //     icon: 'assets/img/PNG/Vector.png'
  //   },
  // ];
  businessContractTabOptions = [
    {
      label: 'Businesses',
      tooltip: 'Click to View Businesses',
      isTable: true,
      value: 'enrolledBusinesses',
      icon: 'assets/img/PNG/show_table.png',
    },
    {
      label: 'Contracts',
      tooltip: 'Click to View Contracts',
      isTable: false,
      value: 'contractsCreated',
      icon: 'assets/img/PNG/Vector.png',
    },
  ];
  businessWiseContractTabOptions = [
    {
      label: 'Summary View',
      tooltip: 'Click to Summary View',
      isTable: true,
      value: 'summaryView',
      icon: 'assets/img/PNG/show_table.png',
      iconName: 'list' as IconName,
    },
    {
      label: 'Detailed View',
      tooltip: 'Click to Detailed View',
      isTable: false,
      value: 'detailedView',
      icon: 'assets/img/PNG/show_table.png',
      iconName: 'betweenHorizontalStart' as IconName,
    },
  ];

  iconColors = [
    'bg-[#7C3AED]',
    'bg-[#3B82F6]',
    'bg-[#10B981]',
    'bg-[#F59E0B]',
    'bg-[#F42D2D]',
    'bg-[#EC4899]',
    'bg-[#FF6900]',
    'bg-[#102FFA]',
  ];

  // Chart colors
  colors: any = {
    // data1: '#a78bfa' as `#${string}` // blue-500 for bars
    data1: '#ac46ffd5' as `#${string}`, // blue-500 for bars
  };
  lineColor = '#fbbf24' as `#${string}`; // amber-400 for retention rate line
  private viewSignal: WritableSignal<'lineChart' | 'barChart'> =
    signal('lineChart');
  private viewCategorySignal: WritableSignal<'table' | 'barChart'> =
    signal('table');
  private viewContactSignal: WritableSignal<
    'enrolledBusinesses' | 'contractsCreated'
  > = signal('enrolledBusinesses');
  private viewBusinessContactSignal: WritableSignal<
    'summaryView' | 'detailedView'
  > = signal('summaryView');
  analysisModalVisible = false;
  analysisModalTitle = '';

  private AI_API_URLs: Record<string, { url: string; title: string }> = {
    generateSummary: { url: '/api/analyze/full-report', title: 'Full Report' },
    activeBusinesses: { url: '/analyze', title: 'Business Growth' },
    overallContractType: {
      url: '/api/analyze/contract-types',
      title: 'Contract Types',
    },
    enrolledBusiness: {
      url: '/api/analyze/regional',
      title: 'Enrolled Businesses',
    },
    industryData: { url: '/api/analyze/industry', title: 'Industry' },
    regionalContracts: {
      url: '/api/analyze/regional',
      title: 'Regional Contracts',
    },
    businessWiseContract: {
      url: '/api/analyze/business-performance',
      title: 'Business Performance',
    },
    overallBusinessContract: {
      url: '/api/analyze/executive-summary',
      title: 'Executive Summary',
    },
  };

  severityClasses: any = {
    high: 'bg-red-100 text-red-800 dark:bg-red-700 dark:text-red-100',
    med: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-700 dark:text-yellow-100',
    low: 'bg-green-100 text-green-800 dark:bg-green-700 dark:text-green-100',
  };

  ngOnInit(): void {
  }



  ngOnDestroy(): void {
    this._commonService.isPanelOpen.set(false);
    // Make sure mobile-only nav state we toggled while opening detail
    // views / bottom sheets is restored on teardown.
    this._commonService.activeAgentId.set(null);
    this._commonService.forceHideMobileNav.set(false);
    this._commonService.showMobileNav.set(true);
    this._commonService.forceHideMobileFloatingBar.set(false);
    this._commonService.showMobileFloatingBar.set(true);
    this._commonService.activeDetailedView.set(null);
  }


  get view() {
    return this.viewSignal();
  }
  get viewCategoryType() {
    return this.viewCategorySignal();
  }
  get viewContactType() {
    return this.viewContactSignal();
  }
  get viewBusinessContactType() {
    return this.viewBusinessContactSignal();
  }



  setActiveTab(tab: any): void {
    this.viewSignal.set(tab);
  }
  setCateoryChartActiveTab(tab: any): void {
    this.viewCategorySignal.set(tab);
  }
  setContactChartActiveTab(tab: any): void {
    this.viewContactSignal.set(tab);
  }

  setBusinessWiseContactChartActiveTab(tab: any): void {
    this.viewBusinessContactSignal.set(tab);
  }

  rangeValues(event: any) { }

  isPrevious: boolean = false;

  /** Controls the "Add hidden section" popover in the toolbar. */
  isAddSectionMenuOpen = signal(false);

  toggleAddSectionMenu(): void {
    this.isAddSectionMenuOpen.update(v => !v);
  }

  /**
   * Returns true when the section should be rendered.
   * Falls back to showing all sections while the layout is loading.
   */
  isVisible(cardId: MonthlyReport1CardId): boolean {
    const items = this.layoutItems();
    if (!items.length) return true;
    return items.find(i => i.id === cardId)?.visible ?? true;
  }

  // ─── Add to My Custom Dashboard (per-card quick action) ────────────────

  /**
   * Returns true when this card has already been pushed to the user's mixed
   * dashboard from this page (either pre-existing on the backend or added
   * during this session). Drives the green "Added" pill on the card.
   */
  isAddedToCustomDashboard(card: DashboardCardLayoutItem<MonthlyReport1CardId>): boolean {
    return this.addedToCustomDashboard().has(card.id);
  }

  /** True while the add-to-custom request for this specific card is in flight. */
  isAddingToCustomDashboard(card: DashboardCardLayoutItem<MonthlyReport1CardId>): boolean {
    return this.addingToCustomDashboardId() === card.id;
  }

  /**
   * Reads the user's mixed ("My") dashboard for the given product and marks
   * every card on this page whose widget is already present and visible.
   * Used as a one-shot rehydration after the engine's `mixed=false` page-load
   * call so the "added" badge survives a page refresh.
   *
   * Defers the actual HTTP call to {@link CustomDashboardPinService} so the
   * mixed-dashboard load logic is shared across every report page.
   */
  private hydrateAddedToCustomDashboardFromServer(productId: string | number): void {
    // Defensive: re-resolve the pid from the sidebar's current selection
    // before firing the request. The caller (constructor effect) passes
    // `cachedProductId()`, which the base engine's response handler can
    // briefly write a stale value into (e.g. denefit=1 from a non-mixed
    // page-load response while credee=5 is actually selected). If the
    // sidebar's selection yields a different pid than the one we were about
    // to send, prefer the sidebar's so the request always reflects the
    // user's CURRENT product.
    const selectedCuid = this.apiService.selectedProductId();
    const resolvedPid = this.resolveActiveProductPid(selectedCuid);
    if (resolvedPid != null && String(resolvedPid) !== String(productId)) {
      productId = resolvedPid;
    }

    this.customDashboardPin
      .hydrateAddedCardIds<MonthlyReport1CardId>(
        this.dashboardEngineConfig.pageId,
        (key) => resolveMonthlyReport1CardFromWidgetKey(key),
        productId,
      )
      .subscribe((addedIds) => {
        if (!addedIds.size) return;
        this.addedToCustomDashboard.update((prev) => {
          const next = new Set(prev);
          for (const id of addedIds) next.add(id);
          return next;
        });
      });
  }

  /* Add to My Custom Dashboard (per-card quick action)   use to add Widget / createDashboard and implemented once inside {@link CustomDashboardPinService.toggleLayoutItem} use customDashboardPin `toggleLayoutItem` to add or remove the widget from the custom 
  dashboard and this wrapper specific product id and the designed default col-span before delegating*/ 

  addToCustomDashboard(card: DashboardCardLayoutItem<MonthlyReport1CardId>): void {
    if (!card || card.widgetId <= 0) return;
    if (this.addingToCustomDashboardId() !== null) return;  
    const productId =
      this.cachedProductId() ??
      this.apiService.selectedProductPid() ??
      this.selectedProductId() ??
      this.apiService.selectedProductId() ??
      null; 
    const resolvedColSpan =
      card.colSpan ?? this.SECTION_COL_SPAN[card.id] ?? 12;

    this.customDashboardPin.toggleLayoutItem({
      pageId: this.dashboardEngineConfig.pageId,
      widgetId: card.widgetId,
      sourcePageId: card.sourcePageId ?? this.dashboardEngineConfig.pageId,
      colSpan: resolvedColSpan,
      rowSpan: card.rowSpan ?? null,
      customConfig: card.customConfig ?? null,
      productId,
      isCurrentlyPinned: this.isAddedToCustomDashboard(card),
      onPinnedChange: (pinned) => {
        this.addedToCustomDashboard.update((s) => {
          const next = new Set(s);
          if (pinned) next.add(card.id);
          else next.delete(card.id);
          return next;
        });
      },
      onPendingChange: (pending) => {
        this.addingToCustomDashboardId.set(pending ? card.id : null);
      },
    });
  }

  // ─── Customize Layout editor (mini-dashboard preview) ──────────────────
  
  openCustomizeLayout(): void {
    const items = this.layoutItems()
      .slice()
      .sort((a, b) => a.order - b.order)
      .map((item, idx) => {
        // Non-resizable cards (col-4, col-6, col-12 on the actual dashboard) 
        const staticSpan = this.SECTION_COL_SPAN[item.id] ?? 12;
        const colSpan = this.canResizeEditorCard(item.id)
          ? this._normalizeColSpan(item.colSpan ?? staticSpan)
          : this._normalizeColSpan(staticSpan);
        return {
          id: item.id,
          title: this.getCardTitle(item),
          visible: item.visible,
          colSpan,
          rowSpan: this._defaultRowSpanFor(item.id, colSpan),
          order: idx,
          widgetId: item.widgetId,
          placementId: item.placementId,
          sourcePageId: item.sourcePageId ?? this.dashboardEngineConfig.pageId,
          customConfig: item.customConfig ?? null,
        };
      });
    this.editorItems.set(items);
    this.editorMixedToggle.set(this.isMixedDashboard());
    // Snapshot the live dashboard layout so we can revert if the user cancels.
    this._layoutSnapshot = this.layoutItems().map((i) => ({ ...i }));
    this.isLayoutEditorOpen.set(true);
  }

  /** State for the inline Customize Layout editor. */
  readonly isLayoutEditorOpen = signal(false);
  readonly editorItems = signal<LayoutEditorItem[]>([]);
  readonly editorMixedToggle = signal(false);

  /** Snapshot of layoutItems taken when the editor opens (used to revert on Cancel). */
  private _layoutSnapshot: ReturnType<typeof this.layoutItems> = [];

  /** Number of cards currently marked visible in the editor draft. */
  readonly editorVisibleCount = computed(
    () => this.editorItems().filter((i) => i.visible).length,
  );

  /** Allowed col-span steps the user can cycle through per card. */
  private readonly COL_SPAN_STEPS: ReadonlyArray<number> = [4, 6, 8, 12];

  /*All cards are now resizable inside the Customize Layout editor — each tile exposes the full 4 / 6 / 8 / 12 width chip set so users can build any responsive arrangement. */

  canResizeEditorCard(_id: MonthlyReport1CardId): boolean {
    return true;
  }

  /** Track-by for the editor cdkDropList. */
  trackEditorItem = (_idx: number, item: LayoutEditorItem): string => item.id;

  /* Tailwind col-span class for a 12-col grid inside the editor preview. Always uses an explicit col-span so the popup mirrors the dashboard. */
  getEditorColSpanClass(span: number): string {
    switch (this._normalizeColSpan(span)) {
      case 3:  return 'col-span-3';
      case 4:  return 'col-span-4';
      case 6:  return 'col-span-6';
      case 8:  return 'col-span-8';
      case 12: return 'col-span-12';
      default: return 'col-span-12';
    }
  }
 
  isTallEditorTile(item: LayoutEditorItem): boolean {
    return (item.rowSpan ?? this._defaultRowSpanFor(item.id, item.colSpan)) >= 2;
  }

  /**
   * Designed (rule-based) row-span for a card at a given width. The only
   * tile that grows to 2 rows by design is TOP_CATEGORIES_BY_CONTRACTS at
   * col-span 8. Every other combination renders as a single-row tile.
   *
   * Used to seed `LayoutEditorItem.rowSpan` on initialisation / reset and
   * to recompute the row-span whenever the user resizes a card directly
   * (so cycling Top Categories back to 8 re-enables the tall layout, and
   * sizing it up to 12 collapses it back down). Drag-drop is the only
   * operation that intentionally bypasses this rule — see
   * {@link onEditorCardDrop} for why.
   */
  private _defaultRowSpanFor(id: MonthlyReport1CardId, span: number): number {
    if (
      id === MonthlyReport1CardId.TOP_CATEGORIES_BY_CONTRACTS &&
      this._normalizeColSpan(span) === 8
    ) {
      return 2;
    }
    return 1;
  }

  closeLayoutEditor(): void {
    // Revert any live visibility changes made via the eye toggle so Cancel
    // truly discards the edits the user previewed.
    if (this._layoutSnapshot.length) {
      this.layoutItems.set(this._layoutSnapshot.map((i) => ({ ...i })));
    }
    this.isLayoutEditorOpen.set(false);
  }

  toggleEditorMixed(): void {
    this.editorMixedToggle.update((v) => !v);
  }

  toggleEditorCardVisibility(id: MonthlyReport1CardId): void {
    this.editorItems.update((items) =>
      reorderLayoutEditorItemsOnVisibilityToggle(items, id),
    );

    this.layoutItems.update((items) =>
      reorderLayoutEditorItemsOnVisibilityToggle(items, id),
    );
  }

  /** Cycles a card through 4 → 6 → 8 → 12 widths in the editor. */
  cycleEditorCardColSpan(id: MonthlyReport1CardId): void {
    this.editorItems.update((items) =>
      items.map((i) => {
        if (i.id !== id) return i;
        const cur = this.COL_SPAN_STEPS.indexOf(i.colSpan);
        const next = this.COL_SPAN_STEPS[(cur + 1) % this.COL_SPAN_STEPS.length];
        return { ...i, colSpan: next, rowSpan: this._defaultRowSpanFor(i.id, next) };
      }),
    );
  }

  /** Sets a specific col-span on a card (used by the inline width selector). Refuses values below the card's designed default. */
  setEditorCardColSpan(id: MonthlyReport1CardId, span: number): void {
    const next = this._normalizeColSpan(span);
    if (this._isBelowDefaultColSpan(id, next)) return;
    this.editorItems.update((items) =>
      items.map((i) => (i.id === id
        ? { ...i, colSpan: next, rowSpan: this._defaultRowSpanFor(i.id, next) }
        : i)),
    );
  }

  /** Steps a card down one width (e.g. 12 → 8 → 6 → 4). Respects each card's designed default as the lower bound. */
  decreaseEditorCardColSpan(id: MonthlyReport1CardId): void {
    this.editorItems.update((items) =>
      items.map((i) => {
        if (i.id !== id) return i;
        const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(i.colSpan));
        if (idx <= 0) return i;
        const nextSpan = this.COL_SPAN_STEPS[idx - 1];
        if (this._isBelowDefaultColSpan(i.id, nextSpan)) return i;
        return { ...i, colSpan: nextSpan, rowSpan: this._defaultRowSpanFor(i.id, nextSpan) };
      }),
    );
  }

  /** Steps a card up one width (e.g. 4 → 6 → 8 → 12). */
  increaseEditorCardColSpan(id: MonthlyReport1CardId): void {
    this.editorItems.update((items) =>
      items.map((i) => {
        if (i.id !== id) return i;
        const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(i.colSpan));
        if (idx < 0 || idx >= this.COL_SPAN_STEPS.length - 1) return i;
        const nextSpan = this.COL_SPAN_STEPS[idx + 1];
        return { ...i, colSpan: nextSpan, rowSpan: this._defaultRowSpanFor(i.id, nextSpan) };
      }),
    );
  }

  /**
   * Whether the minimize button should be active for this card.
   *
   * A card cannot shrink below its **designed default** col-span — e.g. a
   * card designed for `col-8` can only step between 8 and 12, never down to
   * 6 or 4. The button is disabled the moment the card reaches its default
   * width.
   */
  canDecreaseEditorCardColSpan(item: LayoutEditorItem): boolean {
    const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(item.colSpan));
    if (idx <= 0) return false;
    if (this._isBelowDefaultColSpan(item.id, this.COL_SPAN_STEPS[idx - 1])) {
      return false;
    }
    return true;
  }

  /** Whether the maximize button should be active for this card. */
  canIncreaseEditorCardColSpan(item: LayoutEditorItem): boolean {
    const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(item.colSpan));
    return idx >= 0 && idx < this.COL_SPAN_STEPS.length - 1;
  }

  /**
   * Returns `true` when `span` is below the designed default col-span for
   * `id` (sourced from {@link SECTION_COL_SPAN}). Used to enforce per-card
   * shrink floors in the customize-layout stepper.
   */
  private _isBelowDefaultColSpan(id: MonthlyReport1CardId, span: number): boolean {
    const defaultSpan = this.SECTION_COL_SPAN[id];
    if (defaultSpan == null) return false;
    return span < this._normalizeColSpan(defaultSpan);
  }
 
  /**
   * Auto-adjusts widths in the editor so every row fully spans the 12-col
   * grid. Delegates to the shared {@link computeLayoutEditorAutoWidthUpdates}
   * helper, which grows the smallest card in each leaky row (and shares the
   * leftover equally when multiple cards tie at the smallest width). The
   * `isTallEditorTile` callback keeps tall-tile (row-span-2) behaviour
   * honoured during the dense-pack simulation, and the apply step still
   * re-derives each card's `rowSpan` via `_defaultRowSpanFor` so the new
   * width and row footprint stay in sync (e.g. a tall tile that grows past
   * its "tall" threshold collapses back to a single row).
   */
  autoAdjustEditorWidths(): void {
    const updates = computeLayoutEditorAutoWidthUpdates(
      this.editorItems(),
      (i) => this.isTallEditorTile(i),
    );
    if (!updates.size) return;

    this.editorItems.update((arr) =>
      arr.map((i) => {
        if (!updates.has(i.id)) return i;
        const nextSpan = updates.get(i.id)!;
        return {
          ...i,
          colSpan: nextSpan,
          rowSpan: this._defaultRowSpanFor(i.id, nextSpan),
        };
      }),
    );
  }

  /** Whether clicking Auto Adjust Width would actually grow at least one card. */
  canAutoAdjustEditorWidths(): boolean {
    return computeLayoutEditorAutoWidthUpdates(
      this.editorItems(),
      (i) => this.isTallEditorTile(i),
    ).size > 0;
  }

  /**
   * Human-friendly label for a col-span value, used in the editor's
   * width-stepper pill (Small / Medium / Large / Full).
   */
  getEditorColSpanLabel(span: number): string {
    switch (this._normalizeColSpan(span)) {
      case 4:  return 'Small';
      case 6:  return 'Medium';
      case 8:  return 'Large';
      case 12: return 'Full';
      default: return `${span}`;
    }
  }
  /* Swaps the dragged card with the card at the drop target so that ONLY those two tiles exchange positions in the array; every other card keeps its current array slot (no shuffle/cascade like CDK's default `moveItemInArray`). we changed the strategy from shift to pairwise-swap [event, { strategy: 'shift' });]*/
  onEditorCardDrop(event: CdkDragDrop<LayoutEditorItem[]>): void {
    const next = applyLayoutEditorCardDrop(this.editorItems(), event, {
      strategy: 'slot-preserving-swap',
      isResizable: (item) => this.canResizeEditorCard(item.id),
      defaultRowSpan: (item) => this._defaultRowSpanFor(item.id, item.colSpan),
    });
    if (next) this.editorItems.set(next);
  }

  /** Restores the editor draft to the page's default order, widths, and visibility. */
  resetLayoutEditorToDefaults(): void {
    const cardConfig = this.dashboardEngineConfig.cardConfig;
    const layoutById = new Map(this.layoutItems().map((i) => [i.id, i]));

    const defaults = [...cardConfig]
      .sort((a, b) => a.defaultOrder - b.defaultOrder)
      .map((cfg, idx) => {
        const existing = layoutById.get(cfg.id);
        const colSpan = this._normalizeColSpan(this.SECTION_COL_SPAN[cfg.id] ?? 12);
        return {
          id: cfg.id,
          title: cfg.title,
          visible: cfg.defaultVisible,
          colSpan,
          rowSpan: this._defaultRowSpanFor(cfg.id, colSpan),
          order: idx,
          widgetId: existing?.widgetId ?? 0,
          placementId: existing?.placementId,
          sourcePageId: existing?.sourcePageId ?? this.dashboardEngineConfig.pageId,
          customConfig: existing?.customConfig ?? null,
        };
      });

    this.editorItems.set(defaults);
  }

  /**
   * Persists the editor draft. Updates local layout state immediately for a
   * snappy UI, then writes through to the backend (when a dashboard exists)
   * via the batch widgets endpoint.
   */
  applyLayoutEditor(): void {
    const draft = this.editorItems();

    const draftById = new Map(draft.map((d) => [d.id, d]));
    const updatedLocal = this.layoutItems()
      .map((item) => {
        const d = draftById.get(item.id);
        if (!d) return item;
        return {
          ...item,
          visible: d.visible,
          colSpan: d.colSpan,
          order: d.order,
        };
      })
      .sort((a, b) => a.order - b.order)
      .map((item, idx) => ({ ...item, order: idx }));

    this.layoutItems.set(updatedLocal);

    const dashId = this.dashboardId();

    // Send ALL widgets (both visible and hidden) to the backend with their
    // `is_visible` state so the server has the full layout snapshot.
    // Visibility is now controlled exclusively via the `is_visible` flag —
    // no widget is excluded from the payload.
    const allDraft = draft
      .filter((d) => d.widgetId > 0)
      .sort((a, b) => a.order - b.order);

    if (dashId && allDraft.length) {
      this._layoutService
        .batchUpdateWidgets(dashId, {
          widgets: allDraft.map((d) => ({
            widget_id: String(d.widgetId),
            source_page_id: String(
              d.sourcePageId ?? this.dashboardEngineConfig.pageId,
            ),
            col_span: d.colSpan,
            row_span: 1,
            is_visible: d.visible,
            custom_config: d.customConfig ?? {},
          })),
        })
        .pipe(catchError(() => of(null)))
        .subscribe(() => this.loadPersonalizedDashboardLayout());
    }

    if (this.editorMixedToggle() !== this.isMixedDashboard()) {
      this.isMixedDashboard.set(this.editorMixedToggle());
    }

    this._layoutSnapshot = [];
    this.isLayoutEditorOpen.set(false);
  }

  private _normalizeColSpan(span: number | null | undefined): number {
    const allowed = [3, 4, 6, 8, 12];
    if (span == null || !Number.isFinite(span)) return 12;
    if (allowed.includes(span)) return span;
    if (span <= 4) return 4;
    if (span <= 6) return 6;
    if (span <= 8) return 8;
    return 12;
  }
  
  /**
   * Reproduces the master-branch layout via CSS Grid + grid-flow-row-dense:
   *   Row 1:  MOM(col-start-1, span-8) | CONTRACT_TYPES(col-start-9, span-4)
   *   Row 2:  TOP_CATEGORIES(col-start-1, span-8, row-span-2) | ENROLLED(col-start-9, span-4)
   *   Row 3:  TOP_CATEGORIES(still spanning)                  | CONTRACTS_CREATED(col-start-9, span-4)
   *   Row 4+: [Analytics header col-12]  OVERALL(6) | TOP_BUSINESSES(6)  →  TABLE(12)
   *
   * The explicit col-start values ensure each card always occupies the correct
   * column regardless of the order returned by the API.
   */ 
  private readonly SECTION_COL_CLASSES: Partial<Record<MonthlyReport1CardId, string>> = {
    [MonthlyReport1CardId.ACTIVE_BUSINESSES_MOM]:       'col-span-12 lg:col-span-8',
    [MonthlyReport1CardId.CONTRACT_TYPES_OVERVIEW]:     'col-span-12 lg:col-span-4',
    [MonthlyReport1CardId.TOP_CATEGORIES_BY_CONTRACTS]: 'col-span-12 lg:col-span-8 lg:row-span-2',
    [MonthlyReport1CardId.ENROLLED_BUSINESSES]:         'col-span-12 lg:col-span-4',
    [MonthlyReport1CardId.CONTRACTS_CREATED]:           'col-span-12 lg:col-span-4',
    [MonthlyReport1CardId.OVERALL_CONTRACT_TYPE_COUNT]: 'col-span-12 lg:col-span-6',
    [MonthlyReport1CardId.TOP_BUSINESSES_BY_CONTRACTS]: 'col-span-12 lg:col-span-6',
    [MonthlyReport1CardId.BUSINESS_CONTRACT_TABLE]:     'col-span-12',
  };

  /**
   * Numeric col-span (1..12) used by the Customize Layout editor to mirror
   * the real dashboard widths. Falls back to 12 when an id is missing.
   */
  private readonly SECTION_COL_SPAN: Partial<Record<MonthlyReport1CardId, number>> = {
    [MonthlyReport1CardId.ACTIVE_BUSINESSES_MOM]:       8,
    [MonthlyReport1CardId.CONTRACT_TYPES_OVERVIEW]:     4,
    [MonthlyReport1CardId.TOP_CATEGORIES_BY_CONTRACTS]: 8,
    [MonthlyReport1CardId.ENROLLED_BUSINESSES]:         4,
    [MonthlyReport1CardId.CONTRACTS_CREATED]:           4,
    [MonthlyReport1CardId.OVERALL_CONTRACT_TYPE_COUNT]: 6,
    [MonthlyReport1CardId.TOP_BUSINESSES_BY_CONTRACTS]: 6,
    [MonthlyReport1CardId.BUSINESS_CONTRACT_TABLE]:     12,
  };

  /**
   * Returns the responsive col-span class for a section.
   * - When the AI panel is open every card stretches to full width.
   * - When the report is embedded by `my-dashboard` (`mixedMode === true`),
   *   we ALWAYS emit a pure `col-span-12 lg:col-span-{4|6|8|12}` class
   *   (no `row-span` hints, no `col-start` hints) so the rendered cards
   *   line up exactly with what the user configured in the
   *   customize-state dialog. The dialog's preview only knows about
   *   col-span, so honouring the static `row-span-2` on cards like
   *   `TOP_CATEGORIES_BY_CONTRACTS` here would silently shift the live
   *   layout vs. the preview the user just confirmed. Standalone MR1
   *   (mixedMode === false) keeps the original layout exactly as before.
   * - When the card is user-resizable (default col-8) and its persisted
   *   `colSpan` differs from the static default, a dynamic class is used so
   *   width changes saved from the Customize Layout popup are reflected on
   *   the live dashboard.
   * - Otherwise the original static class (with col-start / row-span hints) is used.
   */
  getSectionColClass(card: { id: MonthlyReport1CardId; colSpan?: number | null } | MonthlyReport1CardId): string {
    if (this._commonService.isPanelOpen()) return 'col-span-12';

    const id = typeof card === 'string' ? card : card.id;
    const dynamicSpan = typeof card === 'string' ? null : (card.colSpan ?? null);

    const staticClass = this.SECTION_COL_CLASSES[id] ?? 'col-span-12';
    const staticSpan = this.SECTION_COL_SPAN[id] ?? 12;

    // Embedded mode (my-dashboard / customize-state): always purely col-span
    // so the rendered grid mirrors the customize-dialog preview exactly.
    if (this.mixedMode()) {
      const span = this._normalizeColSpan(dynamicSpan ?? staticSpan);
      return this._dynamicColClass(span);
    }

    if (
      this.canResizeEditorCard(id) &&
      dynamicSpan != null &&
      this._normalizeColSpan(dynamicSpan) !== staticSpan
    ) {
      return this._dynamicColClass(this._normalizeColSpan(dynamicSpan));
    }

    return staticClass;
  }

  /** Static literal classes so Tailwind JIT keeps them in the bundle. */
  private _dynamicColClass(span: number): string {
    switch (span) {
      case 4:  return 'col-span-12 lg:col-span-4';
      case 6:  return 'col-span-12 lg:col-span-6';
      case 8:  return 'col-span-12 lg:col-span-8';
      case 12: return 'col-span-12';
      default: return 'col-span-12';
    }
  }

  /** The three cards that belong under the "Business & Contracts Overall Analytics" header */
  private readonly ANALYTICS_GROUP = new Set<MonthlyReport1CardId>([
    MonthlyReport1CardId.OVERALL_CONTRACT_TYPE_COUNT,
    MonthlyReport1CardId.TOP_BUSINESSES_BY_CONTRACTS,
    MonthlyReport1CardId.BUSINESS_CONTRACT_TABLE,
  ]);

  /**
   * Returns true when `card` is the first visible analytics-group card in the
   * current ordered list, so the section divider header is rendered exactly once.
   *
   * Suppressed in `mixedMode` (the report is embedded inside `my-dashboard`)
   * so the extra `col-span-12` divider row doesn't shift card positions vs.
   * the customize-state preview the user just configured. The standalone MR1
   * page keeps the divider unchanged.
   */
  isFirstAnalyticsCard(card: { id: MonthlyReport1CardId }): boolean {
    if (this.mixedMode()) return false;
    const firstAnalytics = this.orderedDashboardCards().find(c => this.ANALYTICS_GROUP.has(c.id));
    return firstAnalytics?.id === card.id;
  }

  onDateRangeChange(event: DateRangePickerOutput) {
    const {
      start_date,
      end_date,
      previous_start_date,
      previous_end_date,
      unit,
    } = event;

    this.current_start_date = dayjs.utc(start_date).tz(LA_TIMEZONE).format('YYYY-MM-DD');
    this.current_end_date = dayjs.utc(end_date).tz(LA_TIMEZONE).format('YYYY-MM-DD');
    this.unit.set(unit);

    // Fetch Current Period Data
    this.isPrevious = false;
    this.start_date = this.current_start_date;
    this.end_date = this.current_end_date;
    // Mirror plain string props into mobile-template signals.
    this.startDate.set(this.current_start_date);
    this.endDate.set(this.current_end_date);
    this.currentStartDate.set(this.current_start_date);
    this.currentEndDate.set(this.current_end_date);

    const productId = this.selectedProductId();
    if (!productId) return; // Guard against pre-init

    const productConfig = this.productConfigService.getProductConfig(this.productConfigService.getProductName(productId));

    let payload: any = {
      start_time: this.start_date,
      end_time: this.end_date,
      unit: this.unit(),
      overall: 1,
      ...productConfig.payloadExtras,
    };

    this.fetchAllReports(payload);
    this.fetchAllReportsWithoutTimePeriod();

    // If Comparison enabled, fetch Previous Period Data
    if (previous_start_date && previous_end_date) {
      setTimeout(() => {
        this.isPrevious = true;
        this.start_date = dayjs.utc(previous_start_date).format('YYYY-MM-DD');
        this.end_date = dayjs.utc(previous_end_date).format('YYYY-MM-DD');
        let prevPayload: any = {
          start_time: this.start_date,
          end_time: this.end_date,
          unit: this.unit(),
          overall: 1,
          ...productConfig.payloadExtras,
        };

        this.fetchAllReports(prevPayload);
      }, 300);
    } else {
      this.activeBusiness = {
        ...this.activeBusiness,
        previousChartData: [],
      };
    }
  }

  getKeys(obj: any): string[] {
    return obj ? Object.keys(obj) : [];
  }



  private fetchAllReports(payload: any): void {
    const productId = this.selectedProductId();
    if (!productId) return;

    const productConfig = this.productConfigService.getProductConfig(this.productConfigService.getProductName(productId));
    const fetchGeneration = this.reportFetchGeneration;
    const fetchProductId = productId;

    // The original instruction had a `dashboardKey` which is not defined here.
    // Assuming the intent was to log the overall fetch operation.
    if (!this.start_date || !this.end_date) {
      return;
    }

    let options = {
      showErrorToaster: true,
      showSuccessToaster: false,
      showLoader: false,
      cache: true,
      addAccessToken: productConfig.requiresAccessToken,
      productServerName: productConfig.productServerName,
    };

    let endpoints: Array<{
      key: string;
      url: string;
      handler: (res: any) => void;
      sections: MonthlyReport1CardId[];
    }> = [
      {
        key: 'activeBusiness',
        url: '/active_business_stats',
        handler: this.handleActiveBusiness,
        sections: [MonthlyReport1CardId.ACTIVE_BUSINESSES_MOM],
      },
      {
        key: 'contractTypes',
        url: '/contract_types_count',
        handler: this.handleContractTypes,
        sections: [
          MonthlyReport1CardId.CONTRACT_TYPES_OVERVIEW,
          MonthlyReport1CardId.OVERALL_CONTRACT_TYPE_COUNT,
        ],
      },
      {
        key: 'enrolledBusiness',
        url: '/enrolled_by_country',
        handler: this.handleEnrolledBusiness,
        sections: [MonthlyReport1CardId.ENROLLED_BUSINESSES],
      },
      {
        key: 'topCategories',
        url: '/industry_contracts',
        handler: this.handleIndustryContracts,
        sections: [MonthlyReport1CardId.TOP_CATEGORIES_BY_CONTRACTS],
      },
      {
        key: 'regionalContracts',
        url: '/country_contracts',
        handler: this.handleCountryContracts,
        sections: [MonthlyReport1CardId.CONTRACTS_CREATED],
      },
    ].filter((e) => this.isSectionActive(...e.sections));

    for (let { key, url, handler } of endpoints) {
      this.setSectionLoading(key, true);
      const toolsPath = productConfig.reportToolsPath || 'visionboard/tools';
      this.apiService
        .post(`${toolsPath}${url}`, payload, options)
        .subscribe({
          next: (res) => {
            const isStale = fetchGeneration !== this.reportFetchGeneration
              || fetchProductId !== this.selectedProductId();
            if (isStale) {
              this.setSectionLoading(key, false);
              return;
            }
            handler.call(this, res);
            this.setSectionLoading(key, false);
          },
          error: (err) => {
            this.setSectionLoading(key, false);
          },
        });
    }
    this.businessWiseContract.page = 1;
    this.overallBusinessContract.page = 1;
  }

  private fetchAllReportsWithoutTimePeriod(): void {
    this.businessWiseContract.page = 1;
    this.overallBusinessContract.page = 1;
    this.fetchBusinessContracts();
  }

  private handleActiveBusiness(res: any) {
    const startDate = dayjs.utc(this.start_date).startOf('day');
    const endDate = dayjs.utc(this.end_date).endOf('day');
    const unit = this.unit();

    let activeChartData = res.data.unit_data
      .filter((item: any) => {
        const itemStart = dayjs.utc(item.start);
        const itemEnd = dayjs.utc(item.end);
        return (
          (itemEnd.isSame(startDate) || itemEnd.isAfter(startDate)) &&
          (itemStart.isSame(endDate) || itemStart.isBefore(endDate))
        );
      })
      .map((item: any, index: number) => {
        let label = this.formatDateRangeByUnit(item?.start, item?.end, unit);

        // NORMALIZE LABELS FOR COMPARISON
        // If we are showing previous data, we need its labels to match the current period's labels
        // so that the chart can overlay them correctly on the same X-axis positions.
        if (
          this.isPrevious &&
          this.current_start_date &&
          this.current_end_date
        ) {
          const currentUnitData = this.activeBusiness?.chartData || [];
          if (currentUnitData[index]) {
            label = currentUnitData[index].label;
          }
        }

        return {
          start: item.start,
          end: item.end,
          value: item.total || 0,
          label: label,
          key: this.isPrevious ? 'Previous Business' : 'Active Business',
        };
      });

    this.activeBusiness = {
      chartData: !this.isPrevious
        ? activeChartData
        : this.activeBusiness?.chartData || [],
      previousChartData: this.isPrevious
        ? activeChartData
        : this.activeBusiness?.previousChartData || [],
      statsData: !this.isPrevious
        ? res.data.this_unit || {}
        : this.activeBusiness?.statsData || {},
    };
  }

  private formatDateRangeByUnit(
    startDate: string,
    endDate: string,
    unit: string
  ): string {
    const start = dayjs.utc(startDate);
    const end = dayjs.utc(endDate);

    switch (unit) {
      case 'hour':
        // For hourly: show date with hour range
        if (start.isSame(end, 'day')) {
          return `${start.format('DD MMM')} (${start.format(
            'HH:mm'
          )} - ${end.format('HH:mm')})`;
        }
        return `${start.format('DD MMM HH:mm')} - ${end.format(
          'DD MMM HH:mm'
        )}`;

      case 'day':
        // For daily: show date range
        if (start.isSame(end, 'month')) {
          return `${end.format('DD MMM YYYY')}`;
        }
        return `${start.format('DD MMM')} - ${end.format('DD MMM YYYY')}`;

      case 'week':
        // For weekly: show date range
        return `${start.format('DD MMM')} - ${end.format('DD MMM YYYY')}`;

      case 'month':
        // For monthly: show month range
        if (start.isSame(end, 'year')) {
          return `${start.format('MMM')} - ${end.format('MMM YYYY')}`;
        }
        return `${start.format('MMM YYYY')} - ${end.format('MMM YYYY')}`;

      case 'quarter':
        // For quarterly: show quarter range
        return `Q${start.quarter()} ${start.format(
          'YYYY'
        )} - Q${end.quarter()} ${end.format('YYYY')}`;

      case 'year':
        // For yearly: show year range
        if (start.year() === end.year()) {
          return start.format('YYYY');
        }
        return `${start.format('YYYY')} - ${end.format('YYYY')}`;

      default:
        return `${start.format('MMM YYYY')} - ${end.format('MMM YYYY')}`;
    }
  }

  private handleContractTypes(res: any) {
    if (this.isPrevious) {
      if (this.businessContractTypes && this.businessContractTypes.length > 0) {
        this.businessContractTypes = this.businessContractTypes.map((contract: any) => {
          const prevItem = res.unit_data?.find(
            (data: any) => data?.contract_type_name === contract.name
          );
          const prevCount = prevItem ? prevItem.count : 0;
          const currentCount = contract.value;
          const difference = prevCount === 0 ? 0 : Number(((currentCount - prevCount) / prevCount * 100).toFixed(1));
          return {
            ...contract,
            previousValue: prevCount,
            difference: difference,
          };
        });
      }
      return;
    }

    let overall_contract_type = res.overall.map((data: any, index: number) => {
      return {
        'S No': index + 1,
        'Contract Type': data.contract_type_name,
        'Number of contracts': data.count,
      };
    });

    const isComparisonSelected = this.comparisonType() && this.comparisonType() !== 'none';

    this.businessContractTypes = res.unit_data.map(
      (data: any, index: number) => {
        const difference = isComparisonSelected ? 0 : (data?.growth || 0);
        return {
          label: data?.contract_type_name,
          name: data.contract_type_name,
          value: data.count,
          difference: difference,
          previousValue: data?.previous_count,
          bgClass: this.getRandomColor(index),
          icon: 'fileText',
          iconClass: 'text-[#FFFFFF] dark:text-surface-1',
        };
      }
    );

    let data = res.overall.map((item: any) => ({
      label: item.contract_type_name,
      value: Number(item.count ?? 0),
    }));

    this.pieChartData = {
      chartData: data,
    };

    this.updateDashboardSection('overallContractType', overall_contract_type);
  }

  private handleEnrolledBusiness(res: any) {
    let enrolled_business_data = res.data.map((data: any, index: number) => {
      return {
        'S No': index + 1,
        Country: data.country_name,
        Enrolled: data.total,
      };
    });
    this.updateDashboardSection('enrolledBusiness', enrolled_business_data);
  }

  private handleIndustryContracts(res: any) {
    let industry_data = res.data.map((data: any, index: number) => {
      return {
        'S No': index + 1,
        Month: dayjs.utc(res.end_date).format('MM/YYYY'),
        Category: data.ind_name,
        'Contract Created': data.total_contracts,
      };
    });

    // let startDate = res?.start_date;
    // let endDate = res?.end_date;
    // let chartData = res?.data?.map((d: any) => ({
    //   start: startDate,
    //   end: endDate,
    //   value: d?.total_contracts
    // }))

    let chartData = res?.data?.slice(0, 8).map((d: any) => ({
      label: d?.ind_name,
      value: d?.total_contracts,
    }));
    this.topCategoriesChartData = {
      chartData: chartData,
    };

    this.updateDashboardSection('industryData', industry_data);
  }

  private handleCountryContracts(res: any) {
    let regional_contracts = res.data.map((data: any, index: number) => {
      return {
        'S No': index + 1,
        Country: data.country,
        ContractCreated: data.total,
      };
    });
    let startDate = res?.start_date;
    let endDate = res?.end_date;
    let data = res?.data?.map((d: any) => ({
      start: startDate,
      end: endDate,
      label: d?.country,
      value: d?.total,
    }));

    this.contactChartData = {
      chartData: data,
    };

    this.updateDashboardSection('regionalContracts', regional_contracts);
  }



  private fetchBusinessContracts() {
    if (
      !this.isSectionActive(
        MonthlyReport1CardId.TOP_BUSINESSES_BY_CONTRACTS,
        MonthlyReport1CardId.BUSINESS_CONTRACT_TABLE,
      )
    ) {
      return;
    }

    const productId = this.selectedProductId();
    if (!productId) return;

    const productConfig = this.productConfigService.getProductConfig(this.productConfigService.getProductName(productId));
    const fetchGeneration = this.reportFetchGeneration;
    const fetchProductId = productId;
    const toolsPath = productConfig.reportToolsPath || 'visionboard/tools';
    const apiUrl = `${toolsPath}/business_contracts`;

    let payload = {
      start_time: this.start_date,
      end_time: this.end_date,
      overall: 1,
      // Honor the user's current groupBy selection (kept in `this.unit()` by
      // `onDateRangeChange`) instead of re-deriving the granularity from the
      // date range. The previous behavior collapsed long ranges back to a
      // recommended bucket (e.g. "month" for 6m–1y, "year" for >1y) and
      // ignored explicit picks like Quarter / Year.
      unit: this.unit() ||
        this.unitService.getUnitSuggestions(this.start_date, this.end_date)
          .recommended,
    };

    let options = {
      showLoader: false,
      showErrorToaster: true,
      cache: false,
      addAccessToken: productConfig.requiresAccessToken,
      productServerName: productConfig.productServerName,
    };

    this.setSectionLoading('businessWiseContract', true);
    this.setSectionLoading('overallBusinessContract', true);

    this.apiService.post(apiUrl, payload, options).subscribe({
      next: (res: any) => {
        const isStale = fetchGeneration !== this.reportFetchGeneration
          || fetchProductId !== this.selectedProductId();
        if (isStale) {
          this.setSectionLoading('businessWiseContract', false);
          this.setSectionLoading('overallBusinessContract', false);
          return;
        }
        this.setSectionLoading('businessWiseContract', false);
        this.setSectionLoading('overallBusinessContract', false);
        let allData = res.data || [];

        // Handle Business Wise Contract (Summary View)
        this.businessWiseContract.total = allData.length;
        let bwStartIndex = (this.businessWiseContract.page - 1) * this.businessWiseContract.limit;
        this.businessWiseContract.data = allData.map((item: any, i: number) =>
          this.mapBusinessWiseContract(item, i, bwStartIndex)
        );
        this.updateDashboardSection('businessWiseContract', this.businessWiseContract.data);

        // Handle Overall Business Contract (Detailed View)
        this.overallBusinessContract.total = allData.length;
        let obStartIndex = (this.overallBusinessContract.page - 1) * this.overallBusinessContract.limit;
        this.overallBusinessContract.data = allData.map((item: any, i: number) =>
          this.mapOverallBusinessContract(item, i, obStartIndex)
        );
        this.updateDashboardSection('overallBusinessContract', this.overallBusinessContract.data);
      },
      error: (err) => {
        this.setSectionLoading('businessWiseContract', false);
        this.setSectionLoading('overallBusinessContract', false);
      },
    });
  }

  private mapBusinessWiseContract(data: any, index: number, startIndex: number) {
    return {
      'S No': startIndex + index + 1,
      Business: data.business_name,
      Country: data.country_name,
      'No of contracts': data.total_contracts,
    };
  }

  private mapOverallBusinessContract(data: any, index: number, startIndex: number) {
    let typeContracts = data.type_contracts?.reduce((acc: any, d: any) => {
      let cleanedTypeName = d.type_name
        .replace(/ *payment plan */i, '')
        .trim();
      acc[cleanedTypeName] = d.count;
      return acc;
    }, {});

    return {
      'S No': startIndex + index + 1,
      Business: data.business_name,
      Country: data.country_name,
      'Total contracts': data.total_contracts,
      ...typeContracts,
    };
  }

  getContract(key: string) {
    const map: Record<string, any> = {
      businessWiseContract: this.businessWiseContract,
      overallBusinessContract: this.overallBusinessContract,
    };
    return map[key];
  }

  onPageChange(event: any, key: string) {
    switch (key) {
      case 'businessWiseContract':
        this.businessWiseContract.page = event;
        break;
      case 'overallBusinessContract':
        this.overallBusinessContract.page = event;
        break;
    }
    this.fetchBusinessContracts();
  }

  onPageSizeChange(event: any, key: string) {
    switch (key) {
      case 'businessWiseContract':
        this.businessWiseContract.limit = event;
        this.businessWiseContract.page = 1;
        break;
      case 'overallBusinessContract':
        this.overallBusinessContract.limit = event;
        this.overallBusinessContract.page = 1;
        break;
    }
    this.fetchBusinessContracts();
  }

  createTableHeaders(data: any[]): TableHeader[] {
    if (!data || !data.length) return [];
    const firstItem = data[0];
    return Object.keys(firstItem).map((key) => ({
      label: this.formatHeaderLabel(key),
      key,
      align: 'text-left',
    })) as TableHeader[];
  }

  private formatHeaderLabel(key: string): string {
    return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }

  openAnalysisModal(key: string) {
    this.aiSummaryData = {
      summary: '',
      insights: [],
      playbook: [],
      actions: [],
      anomalies: [],
    };
    let mapping = this.AI_API_URLs[key] || null;
    if (!mapping) {
      return;
    }

    // Add key to active requests
    const currentRequests = new Set(this.activeAnalysisRequests());
    currentRequests.add(key);
    this.activeAnalysisRequests.set(currentRequests);

    this.analysisModalTitle = mapping.title;
    let payload = {
      start_time: this.start_date,
      end_time: this.end_date,
      // Use the user's current groupBy selection so AI analysis runs at the
      // same granularity the dashboard is rendering (Quarter / Year, etc.).
      unit: this.unit() ||
        this.unitService.getUnitSuggestions(this.start_date, this.end_date)
          .recommended,
    };

    this.apiService
      .post(mapping.url, payload, {
        productServerName: environment.ai.productServerName,
        showLoader: false,
        showErrorToaster: true,
        errorMessage: 'Failed to get AI analysis.',
        timeout: 2 * 60 * 1000, // 2 minutes
      })
      .subscribe({
        next: (res: any) => {
          // Remove key from active requests
          const updatedRequests = new Set(this.activeAnalysisRequests());
          updatedRequests.delete(key);
          this.activeAnalysisRequests.set(updatedRequests);

          let summaryData: AISummaryData | null = null;
          if (res.summary_parsed) {
            summaryData = res;
          } else if (res.summary && typeof res.summary === 'string') {
            try {
              summaryData = JSON.parse(res.summary);
            } catch (e) {
            }
          } else if (res.module_analyses) {
            summaryData = res.module_analyses;
          }
          if (summaryData) {
            this.aiSummaryData = summaryData;
            const data = { ...this.aiSummaryData };
            this._dialog.open(AiSummaryModalComponent, {
              width: '800px',
              data: key === 'generateSummary' ? this.aiSummaryData : [data],
            });
          } else {
            this.aiSummaryData.summary =
              'Could not parse the analysis from the AI response.';
          }
        },
        error: (err) => {
          // Remove key from active requests on error
          const updatedRequests = new Set(this.activeAnalysisRequests());
          updatedRequests.delete(key);
          this.activeAnalysisRequests.set(updatedRequests);

        },
      });
  }

  isAnalysisLoading(key: string): boolean {
    return this.activeAnalysisRequests().has(key);
  }

  isAnyAnalysisLoading = computed(() => this.activeAnalysisRequests().size > 0);

  initializeTableSections() {
    this.tablesSections = [
      {
        title: 'Contract & Global Reach',
        layout: 'grid',
        cols: 12,
        rows: [
          {
            colSpan: 8,
            data: [],
            subTitle: 'Overall Contract Type Count',
            key: 'overallContractType',
            showAIButton: false,
          },
          {
            colSpan: 4,
            data: [],
            subTitle: 'Enrolled Businesses',
            key: 'enrolledBusiness',
            showAIButton: true,
          },
        ],
      },
      {
        title: 'Top Categories & Regional Contracts',
        layout: 'grid',
        cols: 12,
        rows: [
          {
            colSpan: 8,
            data: [],
            subTitle: 'Top Categories',
            key: 'industryData',
            showAIButton: true,
          },
          {
            colSpan: 4,
            data: [],
            subTitle: 'Contracts Created',
            key: 'regionalContracts',
            showAIButton: true,
          },
        ],
      },
      {
        title: 'Business Contract Analytics',
        layout: 'full',
        data: this.businessWiseContract.data,
        subTitle: 'Businesses wise Contract Created',
        pagination: true,
        key: 'businessWiseContract',
      },
      {
        title: 'Comprehensive Business Overview',
        layout: 'full',
        data: this.overallBusinessContract.data,
        subTitle: 'Overall Business Detail',
        pagination: true,
        key: 'overallBusinessContract',
        showAIButton: true,
      },
    ];
  }

  private updateDashboardSection(key: string, newData: any): void {
    switch (key) {
      case 'overallContractType':
        this.overallContractTypeSignal.set(newData);
        break;

      case 'enrolledBusiness':
        this.enrolledBusinessSignal.set(newData);
        break;

      case 'industryData':
        this.industryDataSignal.set(newData);
        break;

      case 'regionalContracts':
        this.regionalContractsSignal.set(newData);
        break;

      case 'businessWiseContract':
        this.businessWiseContractSignal.set(newData);
        const data = newData.slice(0, 9).map((d: any) => ({
          label: d['Business'],
          value: d['No of contracts'],
        }));

        this.businessWiseContractChartData = {
          chartData: data,
        };

        break;

      case 'overallBusinessContract':
        this.overallBusinessContractSignal.set(newData);
        break;
    }

    for (let section of this.tablesSections) {
      if (section.rows) {
        const row = section.rows.find((r: any) => r.key === key);
        if (row) {
          row.data = newData;
          return;
        }
      }
      if (section.key === key) {
        section.data = newData;
        return;
      }
    }
  }

  totalEnrolled = computed(() =>
    this.enrolledBusinessSignal()?.reduce(
      (sum, item) => sum + (item?.Enrolled ?? 0),
      0
    )
  );
  totalContract = computed(() =>
    this.regionalContractsSignal()?.reduce(
      (sum, item) => sum + (item?.ContractCreated ?? 0),
      0
    )
  );

  getRandomColor(index: number) {
    return this.iconColors[index % this.iconColors.length];
  }

  // All layout methods (loadPersonalizedDashboardLayout, openLayoutManagerDialog,
  // onDashboardCardDrop, toggleDashboardCardVisibility, cycleCardWidth,
  // customizeDashboardCard, confirmResetDashboard, saveLayoutAsNew,
  // setCurrentLayoutDefault, deleteCurrentLayout, toggleMixedMode,
  // openAddWidgetsDialog, getCardTitle, cardWidthPercent, cardAccentStyle,
  // trackDashboardCard, onDashboardLayoutSelected) are inherited from
  // DashboardPageEngine<MonthlyReport1CardId>.

  toolTipData = [
    { label: 'Active Business:', key: 'contractCreated' },
    { label: 'Previous Business:', key: 'registeredBusinesses' },
  ];

  // ─── Mobile responsive support (restored from manisha-mobile_responsive) ───
  // Signals consumed by `app-monthly-report-mobile` and other mobile-only
  // template fragments. They duplicate the existing `start_date` / `end_date`
  // plain props but in signal form so the mobile template can subscribe to
  // changes. `onDateRangeChange()` syncs the two below.
  startDate = signal<string | null>(
    dayjs.utc().tz(LA_TIMEZONE).subtract(6, 'day').startOf('day').format('YYYY-MM-DD')
  );
  endDate = signal<string | null>(
    dayjs.utc().tz(LA_TIMEZONE).endOf('day').format('YYYY-MM-DD')
  );
  currentStartDate = signal<string | null>(null);
  currentEndDate = signal<string | null>(null);
  comparisonType = signal<ComparisonType>('none');
  presetLabel = signal<string | undefined>('Last 7 Days');

  enrolledPrevious = signal(0);
  contractsPrevious = signal(0);

  isDashboardLoading = computed(() => {
    const loading = this.sectionLoading();
    return !!(
      loading['activeBusiness'] ||
      loading['contractTypes'] ||
      loading['enrolledBusiness'] ||
      loading['topCategories'] ||
      loading['regionalContracts'] ||
      loading['businessWiseContract'] ||
      loading['overallBusinessContract']
    );
  });

  enrolledCurrent = computed(() => this.totalEnrolled() || 0);
  contractsCurrent = computed(() => this.totalContract() || 0);

  enrolledGrowth = computed(() => {
    const cur = this.enrolledCurrent();
    const prev = this.enrolledPrevious();
    if (prev === 0) return 0;
    return Number(((cur - prev) / prev * 100).toFixed(1));
  });

  contractsGrowth = computed(() => {
    const cur = this.contractsCurrent();
    const prev = this.contractsPrevious();
    if (prev === 0) return 0;
    return Number(((cur - prev) / prev * 100).toFixed(1));
  });

  top3Businesses = computed(() => {
    const data = this.businessWiseContractSignal() || [];
    return data.slice(0, 3).map((item: any) => ({
      label: item['Business'],
      value: item['No of contracts']
    }));
  });

  podiumConfig = computed(() => {
    const top3 = this.top3Businesses();
    if (!top3.length) return [];

    const styles = [
      {
        index: 1, rank: 2, barHeight: '75%', barColor: 'bg-[#6BA5FF]',
        gradient: 'from-[#4695FF] to-[#92C4FF]',
        circleSize: 'w-10 h-10', circleTop: '-top-[20px]', circleText: 'text-sm',
        textSize: 'text-[10px]', valueSize: 'text-[14px]',
        pt: 'pt-10', pb: 'pb-4', z: ''
      },
      {
        index: 0, rank: 1, barHeight: '95%', barColor: 'bg-[#B66FFF]',
        gradient: 'from-[#AD46FF] to-[#D692FF]',
        circleSize: 'w-12 h-12', circleTop: '-top-[25px]', circleText: 'text-lg',
        textSize: 'text-[12px]', valueSize: 'text-[18px]',
        pt: 'pt-12', pb: 'pb-6', z: 'z-10'
      },
      {
        index: 2, rank: 3, barHeight: '65%', barColor: 'bg-[#FFC56B]',
        gradient: 'from-[#FFAD46] to-[#FFD692]',
        circleSize: 'w-9 h-9', circleTop: '-top-[18px]', circleText: 'text-xs',
        textSize: 'text-[9px]', valueSize: 'text-[13px]',
        pt: 'pt-8', pb: 'pb-3', z: ''
      }
    ];

    return styles
      .map(style => ({ ...style, business: top3[style.index] }))
      .filter(item => !!item.business);
  });

  remainingTopBusinesses = computed(() => {
    const data = this.businessWiseContractSignal() || [];
    return data.slice(3, 10).map((item: any, index: number) => ({
      rank: index + 4,
      label: item['Business'],
      value: item['No of contracts']
    }));
  });

  top3Categories = computed(() => {
    const data = this.industryDataSignal() || [];
    return data.slice(0, 3).map((item: any) => ({
      label: item['Category'],
      value: item['Contract Created']
    }));
  });

  fullCategoryList = computed(() => {
    const data = this.industryDataSignal() || [];
    if (!data.length) return [];

    const maxVal = Math.max(...data.map((item: any) => item['Contract Created'] || 0), 1);

    const colors = [
      'from-[#00A389] to-[#2DD4BF]',
      'from-[#00B4D8] to-[#90E0EF]',
      'from-[#F43F5E] to-[#FB7185]',
      'from-[#F59E0B] to-[#FCD34D]',
      'from-[#7C3AED] to-[#A78BFA]',
      'from-[#3B82F6] to-[#93C5FD]',
      'from-[#F42D2D] to-[#F87171]',
      'from-[#EC4899] to-[#F472B6]',
    ];

    return data.map((item: any, i: number) => ({
      label: item['Category'],
      value: item['Contract Created'],
      percentage: ((item['Contract Created'] || 0) / maxVal) * 100,
      gradient: colors[i % colors.length]
    }));
  });

  maxCategoryValue = computed(() => {
    const top3 = this.top3Categories();
    const maxVal = Math.max(...top3.map((c: any) => c.value), 0);
    return maxVal || 1;
  });

  categoryPodiumConfig = computed(() => {
    const top3 = this.top3Categories();
    const maxValue = this.maxCategoryValue();

    const colors = [
      { bar: 'from-[#00A389] to-[#2DD4BF]', track: 'bg-[#F0FDF4]' },
      { bar: 'from-[#00B4D8] to-[#90E0EF]', track: 'bg-[#F0F9FF]' },
      { bar: 'from-[#F43F5E] to-[#FB7185]', track: 'bg-[#FFF1F2]' }
    ];

    return top3.map((cat: any, i: number) => ({
      ...cat,
      percentage: (cat.value / maxValue) * 100,
      ...colors[i % colors.length]
    }));
  });

  overallContractsTotal = computed(() => {
    this.overallContractTypeSignal();
    const data = this.pieChartData?.chartData || [];
    return data.reduce((sum: number, item: any) => sum + (item.value || 0), 0);
  });

  overallContractsList = computed(() => {
    this.overallContractTypeSignal();
    const data = this.pieChartData?.chartData || [];
    const total = this.overallContractsTotal();

    const colors = [
      '#7C3AED', '#3B82F6', '#EF4444', '#EAB308',
      '#0EA5E9', '#EC4899', '#F59E0B', '#10B981'
    ];

    return data.map((item: any, i: number) => ({
      ...item,
      percentage: total > 0 ? ((item.value / total) * 100).toFixed(1) : '0.0',
      color: colors[i % colors.length]
    }));
  });

  mobileDetailedView = signal<string | null>(null);
  mobileChartType = signal<'bar' | 'line'>('bar');
  enrolledSearchQuery = signal<string>('');
  contractsSearchQuery = signal<string>('');

  fullEnrolledBusinessList = computed(() => {
    const data = this.enrolledBusinessSignal() || [];
    const search = this.enrolledSearchQuery().toLowerCase();

    return data
      .filter((item: any) => (item?.Country || '').toLowerCase().includes(search))
      .map((item: any) => ({
        name: item.Country,
        value: item.Enrolled,
        icon: 'https://flagcdn.com/w80/' + (this.getCountryCode(item.Country) || 'us') + '.png'
      }));
  });

  fullRegionalContractsList = computed(() => {
    const data = this.regionalContractsSignal() || [];
    const search = this.contractsSearchQuery().toLowerCase();

    return data
      .filter((item: any) => (item?.Country || '').toLowerCase().includes(search))
      .map((item: any) => ({
        name: item.Country,
        value: item.ContractCreated,
        icon: 'https://flagcdn.com/w80/' + (this.getCountryCode(item.Country) || 'us') + '.png'
      }));
  });

  getCountryCode(countryName: string): string {
    const map: Record<string, string> = {
      'United States': 'us',
      'USA': 'us',
      'United Kingdom': 'gb',
      'UK': 'gb',
      'Canada': 'ca',
      'Australia': 'au',
      'Germany': 'de',
      'France': 'fr',
      'India': 'in',
    };
    return map[countryName] || 'un';
  }

  get maxActiveBusinessValue(): number {
    if (!this.activeBusiness?.chartData?.length) return 0;
    return Math.max(...this.activeBusiness.chartData.map((d: any) => d.value || 0));
  }

  openMobileDetail(type: string) {
    this.mobileDetailedView.set(type);
    this._commonService.activeDetailedView.set(type);
    this._commonService.forceHideMobileNav.set(true);
    this._commonService.showMobileNav.set(false);
    this._commonService.forceHideMobileFloatingBar.set(true);
    this._commonService.showMobileFloatingBar.set(false);
  }

  closeMobileDetail() {
    this.mobileDetailedView.set(null);
    this._commonService.activeDetailedView.set(null);
    this._commonService.forceHideMobileNav.set(false);
    this._commonService.showMobileNav.set(true);
    this._commonService.forceHideMobileFloatingBar.set(false);
    this._commonService.showMobileFloatingBar.set(true);
  }

  setMobileChartType(type: 'bar' | 'line') {
    this.mobileChartType.set(type);
  }

  openFilterSheet() {
    this._commonService.forceHideMobileNav.set(true);
    this._commonService.showMobileNav.set(false);
    this._commonService.forceHideMobileFloatingBar.set(true);
    this._commonService.showMobileFloatingBar.set(false);
    const startStr = this.datePipe.transform(this.startDate(), 'MMM dd, yyyy');
    const endStr = this.datePipe.transform(this.endDate(), 'MMM dd, yyyy');
    const dateSubtitle = startStr && endStr ? `${startStr} - ${endStr}` : '';

    const sheetRef = this._bottomSheet.open(MobileSheetComponent, {
      panelClass: 'mobile-sheet-wrapper-container',
      data: {
        title: 'Filter',
        subtitle: dateSubtitle,
        icon: 'calendar',
        component: DatePickerComponent,
        data: {
          isMobileMode: true,
          value: {
            startDate: this.startDate(),
            endDate: this.endDate(),
            unit: this.unit(),
            comparisonType: this.comparisonType(),
            presetLabel: this.presetLabel()
          }
        }
      } as MobileSheetData
    });

    sheetRef.afterDismissed().subscribe((result: DateRangePickerOutput) => {
      if (!this.mobileDetailedView()) {
        this._commonService.forceHideMobileNav.set(false);
        this._commonService.showMobileNav.set(true);
        this._commonService.forceHideMobileFloatingBar.set(false);
        this._commonService.showMobileFloatingBar.set(true);
      }
      if (result) {
        this.onDateRangeChange(result);
      }
    });
  }

  openAiBottomSheet() {
    this.openAiAssistant(false);
    this._commonService.forceHideMobileNav.set(true);
    this._commonService.showMobileNav.set(false);
    this._commonService.forceHideMobileFloatingBar.set(true);
    this._commonService.showMobileFloatingBar.set(false);
    const sheetRef = this._bottomSheet.open(MobileSheetComponent, {
      panelClass: ['mobile-sheet-wrapper-container', 'is-full-height'],
      data: {
        title: 'Ask Prism AI',
        icon: 'sparkles',
        component: AiChatPanelComponent,
        isFullHeight: true,
        showClose: true,
        data: {
          layoutMode: 'fullscreen'
        }
      } as MobileSheetData
    });

    sheetRef.afterDismissed().subscribe(() => {
      if (!this.mobileDetailedView()) {
        this._commonService.forceHideMobileNav.set(false);
        this._commonService.showMobileNav.set(true);
        this._commonService.forceHideMobileFloatingBar.set(false);
        this._commonService.showMobileFloatingBar.set(true);
      }
    });
  }

  openAiAssistant(openPanel: boolean = true) {
    const productId = this.selectedProductId();
    if (!productId) return;

    const productName = this.productConfigService.getProductName(productId).toLowerCase();
    const currentRoute = this.router.url.split('?')[0];
    const pageConfig = this.headlinePageService.getAllConfigs().find((c: any) => currentRoute.endsWith(c.route) || currentRoute.includes(c.route));
    const pageKey = pageConfig ? pageConfig.key : '';

    this._commonService.aiSuggestions.set([
      `What is this week's progress?`,
      `Compare this month's progress with last month?`,
      `Compare business growth with contracts growth for last quarter?`,
      `What is this year's progress?`
    ]);
    this._commonService.chatType.set('MONTHLY_REPORT_1');
    this._commonService.isPanelOpen.set(openPanel);

    this.aiBotService.resolveAgent(productName, pageKey, 'ask_prism_ai').subscribe({
      next: (res: any) => {
        const agentId = res?.agent_id || res?.data?.agent_id;
        if (agentId) {
          this._commonService.activeAgentId.set(agentId);
        } else {
          this._commonService.activeAgentId.set(null);
        }
      },
      error: () => {
        this._commonService.activeAgentId.set(null);
      }
    });
  }

}
